const StyleB = () => {
  return (
    <div className="style-b">
      <style>{`
        .style-b {
          --bg-primary: #f9f6f0;
          --bg-secondary: #f0ebe2;
          --bg-accent: #8b4513;
          --text-primary: #2c1810;
          --text-secondary: #6d4c36;
          --text-light: #a0805a;
          --accent-primary: #b8860b;
          --accent-secondary: #cd853f;
          --border: #d4c4a8;
          --shadow: rgba(139, 69, 19, 0.1);
        }
        
        .style-b.dark {
          --bg-primary: #1f1611;
          --bg-secondary: #2a1f18;
          --bg-accent: #654321;
          --text-primary: #f4f0e8;
          --text-secondary: #c8b896;
          --text-light: #9e8a6f;
          --accent-primary: #daa520;
          --accent-secondary: #cd853f;
          --border: #3d2f22;
          --shadow: rgba(218, 165, 32, 0.1);
        }

        .style-b {
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: 'Georgia', serif;
          line-height: 1.6;
        }
        
        .style-b h1, .style-b h2, .style-b h3 {
          font-family: 'Georgia', serif;
          font-weight: 700;
          color: var(--text-primary);
        }
        
        .style-b h1 {
          font-size: 3rem;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          text-shadow: 1px 1px 2px var(--shadow);
        }
        
        .style-b h2 {
          font-size: 2.2rem;
          line-height: 1.2;
          color: var(--bg-accent);
          margin-bottom: 1rem;
        }
        
        .style-b h3 {
          font-size: 1.4rem;
          color: var(--accent-primary);
          margin-bottom: 0.75rem;
        }
        
        .style-b .heritage-card {
          background: var(--bg-secondary);
          border: 2px solid var(--border);
          border-radius: 8px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 8px var(--shadow);
          position: relative;
        }
        
        .style-b .heritage-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--accent-primary), var(--accent-secondary));
        }
        
        .style-b .ornamental {
          border-left: 4px solid var(--accent-primary);
          padding-left: 1.5rem;
          font-style: italic;
          color: var(--text-secondary);
        }
        
        .style-b .heritage-button {
          background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
          color: var(--bg-primary);
          border: none;
          padding: 1rem 2rem;
          border-radius: 6px;
          font-family: Georgia, serif;
          font-weight: 600;
          box-shadow: 0 2px 4px var(--shadow);
          transition: all 0.3s ease;
        }
        
        .style-b .heritage-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px var(--shadow);
        }
        
        .style-b .texture-bg {
          background-image: 
            radial-gradient(circle at 1px 1px, var(--text-light) 1px, transparent 0);
          background-size: 20px 20px;
          opacity: 0.05;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
        }
      `}</style>
      
      <div className="max-w-5xl mx-auto px-8 py-12" style={{position: 'relative'}}>
        <div className="texture-bg"></div>
        
        <header className="text-center" style={{marginBottom: '4rem'}}>
          <h1>Cultural Heritage</h1>
          <div className="ornamental" style={{maxWidth: '700px', margin: '0 auto', fontSize: '1.2rem'}}>
            Drawing inspiration from centuries of archival tradition, this design honors 
            the rich textures and warm tones of historical documents and manuscripts.
          </div>
        </header>

        <section style={{marginBottom: '4rem'}}>
          <div className="heritage-card">
            <h2>Archive-Inspired Aesthetics</h2>
            <p style={{color: 'var(--text-secondary)', marginBottom: '1.5rem', fontSize: '1.1rem'}}>
              Our visual language draws from the materials we preserve: the warm amber of aged paper, 
              the rich brown of leather bindings, the golden patina of historical photographs. 
              Every color choice connects to the <span style={{color: 'var(--accent-primary)', fontWeight: '600'}}>
              tangible heritage</span> of cultural institutions.
            </p>
            
            <div style={{display: 'grid', gap: '1.5rem', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'}}>
              <div style={{textAlign: 'center'}}>
                <div style={{
                  width: '60px', 
                  height: '60px', 
                  background: 'var(--accent-primary)', 
                  borderRadius: '50%', 
                  margin: '0 auto 0.5rem',
                  boxShadow: '0 2px 4px var(--shadow)'
                }}></div>
                <h3>Warm Golds</h3>
                <p style={{color: 'var(--text-light)', fontSize: '0.9rem'}}>
                  Inspired by illuminated manuscripts
                </p>
              </div>
              
              <div style={{textAlign: 'center'}}>
                <div style={{
                  width: '60px', 
                  height: '60px', 
                  background: 'var(--bg-accent)', 
                  borderRadius: '50%', 
                  margin: '0 auto 0.5rem',
                  boxShadow: '0 2px 4px var(--shadow)'
                }}></div>
                <h3>Rich Browns</h3>
                <p style={{color: 'var(--text-light)', fontSize: '0.9rem'}}>
                  Echoing leather and wood
                </p>
              </div>
              
              <div style={{textAlign: 'center'}}>
                <div style={{
                  width: '60px', 
                  height: '60px', 
                  background: 'var(--bg-secondary)', 
                  border: '3px solid var(--border)',
                  borderRadius: '50%', 
                  margin: '0 auto 0.5rem',
                  boxShadow: '0 2px 4px var(--shadow)'
                }}></div>
                <h3>Parchment Tones</h3>
                <p style={{color: 'var(--text-light)', fontSize: '0.9rem'}}>
                  Aged paper and vellum
                </p>
              </div>
            </div>
          </div>
        </section>

        <section style={{marginBottom: '4rem'}}>
          <h2 style={{textAlign: 'center', marginBottom: '2rem'}}>Typography & Texture</h2>
          
          <div style={{display: 'grid', gap: '2rem', gridTemplateColumns: '1fr 1fr'}}>
            <div className="heritage-card">
              <h3>Serif Tradition</h3>
              <p style={{color: 'var(--text-secondary)'}}>
                Georgia serif typography connects us to the tradition of scholarly publishing, 
                while maintaining excellent readability across digital platforms.
              </p>
            </div>
            
            <div className="heritage-card">
              <h3>Subtle Textures</h3>
              <p style={{color: 'var(--text-secondary)'}}>
                Gentle patterns and shadows suggest the tactile quality of archival materials 
                without overwhelming the digital experience.
              </p>
            </div>
          </div>
        </section>

        <section className="text-center" style={{padding: '3rem 0'}}>
          <div className="ornamental" style={{maxWidth: '600px', margin: '0 auto 2rem', fontSize: '1.1rem'}}>
            Perfect for institutions that want to honor their historical roots while 
            embracing digital transformation.
          </div>
          <button className="heritage-button">
            Discover Our Heritage Approach
          </button>
        </section>
      </div>
    </div>
  );
};

export default StyleB;